import { NICHES, rows, userVisible } from "./appData";
const NICHE_ALIASES = {
  filmmaker: [
    "filmmaker",
    "film maker",
    "videographer",
    "cinematographer",
    "director of photography",
    "dop",
  ],
  "content creator": ["content creator", "creator", "digital creator"],
  editor: [
    "editor",
    "edit",
    "video editor",
    "post editor",
    "post production",
    "post-production",
  ],
  gaffer: ["gaffer", "lighting"],
  "drone operator": ["drone operator", "drone pilot", "drone"],
  "vfx artists": ["vfx artist", "vfx artists", "visual effects", "vfx"],
  colorist: [
    "colorist",
    "colourist",
    "color grading",
    "colour grading",
    "grader",
  ],
  "sound designer": ["sound designer", "sound design", "audio designer"],
  business: ["business", "business owner", "entrepreneur"],
  producer: ["producer", "film producer", "line producer"],
  director: ["director", "film director"],
  grip: ["grip", "key grip", "best boy grip"],
  "production designer": ["production designer", "production design"],
  "makeup artist": ["makeup artist", "make-up artist", "mua", "makeup"],
  actor: ["actor"],
  actress: ["actress"],
  trainee: ["trainee", "intern", "apprentice"],
  photographer: ["photographer", "photography"],
};
export const PAGE_SIZE = 20;
export const displayName = (user) =>
  [user?.name, user?.displayName, user?.username]
    .find((n) => typeof n === "string" && n.trim())
    ?.trim() || "Unknown";
export const timestamp = (value) =>
  typeof value === "number"
    ? value
    : Date.parse(value) ||
      Date.parse(String(value || "").replace(" ", "T")) ||
      0;
const normalize = (value) => {
  const cleaned = String(value || "")
    .trim()
    .toLowerCase();
  return (
    Object.entries(NICHE_ALIASES).find(([, aliases]) =>
      aliases.includes(cleaned),
    )?.[0] || cleaned
  );
};
export const nicheLabel = (value) =>
  NICHES.find((n) => normalize(n) === normalize(value)) || "";
export function profileNiches(user) {
  const raw = Array.isArray(user?.niches)
    ? user.niches
    : user?.niche
      ? [user.niche]
      : [];
  return [
    ...new Set(
      raw
        .map((n) => nicheLabel(typeof n === "string" ? n : ""))
        .filter(Boolean),
    ),
  ].slice(0, 3);
}
export const activeNow = (user, now = Date.now()) =>
  Boolean(
    user?.isOnline ||
    (timestamp(user?.lastSeen) >= now - 15 * 60 * 1000 &&
      timestamp(user?.lastSeen) > 0),
  );
export const mutualCount = (mine, user) =>
  Object.keys(mine || {}).filter((id) =>
    Boolean(mine[id] && user?.friends?.[id]),
  ).length;
export function friendStatus(me, person, myId) {
  if (me?.friends?.[person.id]) return "friends";
  if (me?.friendRequests?.[person.id] === "sent") return "sent";
  if (
    me?.friendRequests?.[person.id] === "received" ||
    person?.friendRequests?.[myId] === "sent"
  )
    return "received";
  return "none";
}
export function friendshipPatch(myId, otherId, status, now = Date.now()) {
  if (!myId || !otherId || myId === otherId)
    throw new Error("Please sign in again before updating friends.");
  const base = {
    [`Users/${myId}/friendRequests/${otherId}`]: null,
    [`Users/${otherId}/friendRequests/${myId}`]: null,
  };
  if (status === "received")
    return {
      ...base,
      [`Users/${myId}/friends/${otherId}`]: {
        createdAt: new Date(now).toISOString(),
      },
      [`Users/${otherId}/friends/${myId}`]: {
        createdAt: new Date(now).toISOString(),
        acceptedBy: myId,
      },
    };
  if (status === "friends")
    return {
      ...base,
      [`Users/${myId}/friends/${otherId}`]: null,
      [`Users/${otherId}/friends/${myId}`]: null,
    };
  if (status === "sent") return base;
  return {
    [`Users/${myId}/friendRequests/${otherId}`]: "sent",
    [`Users/${otherId}/friendRequests/${myId}`]: "received",
  };
}
export function discoverModel(
  data,
  myId,
  niche = "All",
  query = "",
  now = Date.now(),
) {
  const me = data?.[myId] || {};
  const users = rows(data).filter(
    (u) => u.id !== myId && userVisible(u) && !me.blockedUsers?.[u.id],
  );
  const search = query.trim().toLowerCase();
  const pool = users.filter(
    (u) =>
      !search ||
      [
        displayName(u),
        u.username,
        u.bio,
        u.role,
        nicheLabel(u.niche),
        ...profileNiches(u),
      ].some((v) =>
        String(v || "")
          .toLowerCase()
          .includes(search),
      ),
  );
  const selected = pool.filter(
    (u) =>
      niche === "All" ||
      profileNiches(u).includes(niche) ||
      (!profileNiches(u).length && nicheLabel(u.niche) === niche),
  );
  const counts = new Map();
  pool.forEach((u) => {
    const labels = profileNiches(u);
    if (!labels.length && nicheLabel(u.niche)) labels.push(nicheLabel(u.niche));
    new Set(labels).forEach((label) =>
      counts.set(label, (counts.get(label) || 0) + 1),
    );
  });
  return {
    me,
    users,
    pool,
    selected,
    chips: [
      { label: "All", count: pool.length },
      ...NICHES.map((label) => ({ label, count: counts.get(label) || 0 })),
    ],
    online: selected
      .filter((u) => me.friends?.[u.id] && activeNow(u, now))
      .slice(0, 12),
    newest: [...selected]
      .sort((a, b) => timestamp(b.createdAt) - timestamp(a.createdAt))
      .slice(0, 8),
    ranked: [...selected].sort(
      (a, b) =>
        mutualCount(me.friends, b) - mutualCount(me.friends, a) ||
        timestamp(b.createdAt) - timestamp(a.createdAt),
    ),
    friends: users.filter((u) => me.friends?.[u.id]),
  };
}
